import { Controller } from 'stimulus'

/**
 * After a class card is removed via turbo stream, updates the day count,
 * removes an empty day group, and shows the empty state when no cards remain.
 *
 * Markup (Stimulus 1):
 *   data-controller="community-classes--classes-timeline"
 *   data-target="community-classes--classes-timeline.list"
 *   data-target="community-classes--classes-timeline.emptyTemplate" (optional; used when leaving last class)
 *   .classes-day > .classes-day-count + .class-card
 */
export default class extends Controller {
  static targets = ['list', 'emptyTemplate']

  connect() {
    this.onBeforeStreamRender = this.onBeforeStreamRender.bind(this)
    document.addEventListener('turbo:before-stream-render', this.onBeforeStreamRender)
  }

  disconnect() {
    document.removeEventListener('turbo:before-stream-render', this.onBeforeStreamRender)
  }

  onBeforeStreamRender(event) {
    const originalRender = event.detail.render

    event.detail.render = async (streamElement) => {
      const day = this.dayForRemovedCard(streamElement)
      await originalRender(streamElement)
      if (day) this.afterCardRemoved(day)
    }
  }

  dayForRemovedCard(streamElement) {
    if (streamElement.action !== 'remove') return null
    if (!this.hasListTarget) return null

    const targetId = streamElement.getAttribute('target')
    if (!targetId) return null

    const card = this.listTarget.querySelector(`[id="${targetId}"]`)
    if (!card?.classList.contains('class-card')) return null

    return card.closest('.classes-day')
  }

  afterCardRemoved(day) {
    if (!day.isConnected) return

    const cards = day.querySelectorAll('.class-card')
    if (cards.length === 0) {
      day.remove()
    } else {
      const count = day.querySelector('.classes-day-count')
      if (count) count.textContent = this.pluralizeClasses(cards.length)
    }

    this.showEmptyStateIfNeeded()
  }

  showEmptyStateIfNeeded() {
    if (!this.hasListTarget) return
    if (this.listTarget.querySelector('.class-card') !== null) return
    if (!this.hasEmptyTemplateTarget) return

    const empty = this.emptyTemplateTarget.content.firstElementChild.cloneNode(true)
    this.listTarget.replaceWith(empty)
    this.emptyTemplateTarget.remove()
  }

  pluralizeClasses(count) {
    return `${count} class${count === 1 ? '' : 'es'}`
  }
}
